// Composable para utilidades generales
// Funciones que no encajan en categorías específicas (fechas, validación, etc.)

import gwtApi from "@wkt/api/gwt/gwtAPI";
import { Constants } from "@wkt/components/common/Constants";
import { useWorktimeStore } from "@wkt/stores/worktime";
import type { AdminProfile } from "@wkt/api/gwt/gwtDTO";
import moment from "moment";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useI18n } from "vue-i18n";

/**
 * Composable para utilidades generales del sistema
 * Incluye funciones de permisos, assets, tiempo, etc.
 */
export function useUtils() {
  const { t, locale: i18nLocale } = useI18n();
  const worktimeStore = useWorktimeStore();

  // ============================================
  // STATE REACTIVO
  // ============================================

  const locale = ref<string>(navigator.language || "en");
  const currentTime = ref<string>(moment().format("LLLL"));
  const lastAdminCompany = ref<number>(-1);
  const constants = Constants;

  let timeInterval: number | undefined;

  // ============================================
  // COMPUTED PROPERTIES
  // ============================================

  const uuid = computed(() => worktimeStore.uuid);
  const deviceInfo = computed(() => worktimeStore.deviceInfo);
  const turns = computed(() => worktimeStore.turns);

  // ============================================
  // LIFECYCLE HOOKS
  // ============================================

  onMounted(() => {
    timeInterval = window.setInterval(updateTimeInterval, 1000);
    moment.locale(locale.value);
  });

  onUnmounted(() => {
    if (timeInterval) {
      clearInterval(timeInterval);
      timeInterval = undefined;
    }
  });

  // ============================================
  // MÉTODOS DE ROLES Y PERMISOS
  // ============================================

  /**
   * Verifica si un perfil tiene al menos uno de los roles especificados
   * @param profile - Perfil de administrador
   * @param roles - Array de nombres de roles a verificar
   * @returns true si tiene al menos uno de los roles con permiso 1 o 2
   */
  function matchRole(
    profile: AdminProfile | undefined,
    roles: string[]
  ): boolean {
    if (!profile) return false;

    for (const role of roles) {
      const permissionName = `module${role}`;
      let permissionValue = -1;

      for (const key of Object.keys(profile)) {
        if (key.toLowerCase() === permissionName.toLowerCase()) {
          permissionValue = profile[key];
          break;
        }
      }

      if (permissionValue === 1 || permissionValue === 2) {
        return true;
      }
    }

    return false;
  }

  /**
   * Verifica si un perfil tiene permiso de modificación para al menos uno de los roles
   * @param profile - Perfil de administrador
   * @param roles - Array de nombres de roles a verificar
   * @returns true si tiene al menos uno de los roles con permiso 2 (modificar)
   */
  function matchRoleModify(
    profile: AdminProfile | undefined,
    roles: string[]
  ): boolean {
    if (!profile) return false;

    for (const role of roles) {
      const permissionName = `module${role}`;
      let permissionValue = -1;

      for (const key of Object.keys(profile)) {
        if (key.toLowerCase() === permissionName.toLowerCase()) {
          permissionValue = profile[key];
          break;
        }
      }

      if (permissionValue === 2) {
        return true;
      }
    }

    return false;
  }

  // ============================================
  // MÉTODOS DE UTILIDAD
  // ============================================

  /**
   * Traduce una clave si empieza con "wkt.", sino devuelve el valor original
   * @param key - Clave de traducción o texto literal
   */
  function st(key: string): string {
    if (key && key.indexOf && key.indexOf("wkt.") === 0) {
      return t(key);
    }
    return key;
  }

  /**
   * Actualiza el tiempo actual (llamado por el intervalo)
   */
  function updateTimeInterval(): void {
    currentTime.value = moment().format("LLLL");
  }

  /**
   * Verifica si la compañía del admin ha cambiado (necesita refrescar datos)
   */
  function mustRefresh(): boolean {
    const session = gwtApi.getSessionInformation();
    const currentAdminCompany = session?.company?.id || -1;
    const mustRefreshValue =
      lastAdminCompany.value > 0 &&
      lastAdminCompany.value !== currentAdminCompany;
    lastAdminCompany.value = currentAdminCompany;
    return mustRefreshValue;
  }

  /**
   * Obtiene el nombre completo de un empleado
   * @param employee - Objeto empleado con firstname y lastname
   */
  function getEmployeeFullname(employee: unknown): string {
    if (!employee) return "---";
    const emp = employee as { lastname?: string; firstname?: string };
    return `${emp.lastname}, ${emp.firstname}`;
  }

  /**
   * Obtiene la URL de un asset de perfil (avatar) desde Google Cloud Storage
   * @param fileName - Nombre del archivo
   */
  function getProfileAssetUrl(fileName: string | null | undefined): string {
    if (!fileName) return "img/default-avatar.jpeg";
    const bucketName = "worktime-dev"; // TODO: obtener de configuración
    return `https://storage.googleapis.com/${bucketName}/${fileName}`;
  }

  /**
   * Obtiene la URL de un asset público desde Google Cloud Storage
   * @param fileName - Nombre del archivo
   */
  function getPublicAssetUrl(fileName: string): string {
    const bucketName = "worktime-dev"; // TODO: obtener de configuración
    return `https://storage.googleapis.com/${bucketName}/${fileName}`;
  }

  /**
   * Formatea un valor monetario según el locale del usuario
   */
  function formatCurrency(value: unknown): string {
    if (value === null || value === undefined) return "";

    const stringValue = String(value);
    
    // Obtener información de sesión para locale (requiere gwtApi)
    // Por ahora usar el locale de i18n
    const localeStr = i18nLocale.value || "en";

    const numFormat = new Intl.NumberFormat(localeStr, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const decimalSeparator = numFormat.formatToParts(1.1)[1].value;

    if (decimalSeparator === "." && stringValue.indexOf(",") !== -1) {
      return stringValue.replace(/,/g, ".");
    } else if (decimalSeparator === "," && stringValue.indexOf(".") !== -1) {
      return stringValue.replace(/\./g, ",");
    }

    return stringValue;
  }

  function formatNumberToLocale(value: unknown): string {
    if (value === null || value === undefined) return "";

    // Convertir a string y limpiar
    let stringValue = String(value).trim();
    
    if (!stringValue) return "";

    // Obtener el locale actual
    const localeStr = i18nLocale.value || "en";

    // Crear formateador para obtener el separador decimal del locale
    const numFormat = new Intl.NumberFormat(localeStr, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const decimalSeparator = numFormat.formatToParts(1.1)[1].value;

    // Si el locale usa coma como separador decimal, reemplazar punto por coma
    if (decimalSeparator === ",") {
      stringValue = stringValue.replace(".", ",");
    }

    return stringValue;
  }

    /**
   * Convierte un nombre de color a código hexadecimal
   * @param color - Nombre del color (ej: "red darken-2")
   * @returns Código hexadecimal del color o el color original si no se encuentra
   */
  function convertColorToHex(color: string | null): string | null {
    if (!color) {
      return null;
    }

    const colorParts = color.split(" ");
    let colorId = colorParts[0].replace("-", "").toUpperCase();

    if (colorParts.length === 2) {
      colorId += `-${colorParts[1].toUpperCase()}`;
    }

    const index = Constants.COLORS.findIndex(
      (c: { id: string; hex: string }) => c.id === colorId
    );

    if (index === -1) {
      return color;
    }

    return Constants.COLORS[index].hex;
  }

  return {
    // State
    locale,
    currentTime,
    lastAdminCompany,
    constants,

    // Computed
    uuid,
    deviceInfo,
    turns,

    // Roles y permisos
    matchRole,
    matchRoleModify,

    // Utilidades
    st,
    updateTimeInterval,
    mustRefresh,
    getEmployeeFullname,
    getProfileAssetUrl,
    getPublicAssetUrl,
    formatCurrency,
    formatNumberToLocale,
    convertColorToHex,
  };
}
